import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, Undo2, Trash2, FileText, Download, Printer } from "lucide-react";
import { formatCurrency } from "@/lib/format";
import { useSettings } from "@/hooks/useSettings";
import { toast } from "sonner";
import { buildInvoicePdf, invoiceNoFromOrder } from "@/lib/invoice";

const STATUSES = ["pending", "processing", "shipped", "delivered", "cancelled"];

const PaymentBadge = ({ status }: { status: string }) => {
  const isReceived = status === "received";
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium capitalize ${
        isReceived
          ? "bg-success/15 text-success-foreground"
          : "bg-warning/15 text-warning-foreground"
      }`}
    >
      {isReceived ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
      {status}
    </span>
  );
};

const AdminOrders = () => {
  const settings = useSettings();
  const symbol = settings.currency_symbol || "৳";
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);
  const [items, setItems] = useState<any[]>([]);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [undoId, setUndoId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [marking, setMarking] = useState(false);

  const load = async () => {
    const { data } = await supabase.from("orders").select("*").order("created_at", { ascending: false });
    setOrders(data ?? []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const totalRevenue = orders
    .filter((o) => o.payment_status === "received")
    .reduce((s, o) => s + Number(o.total_amount), 0);
  const pendingCount = orders.filter((o) => o.payment_status !== "received").length;

  const updateStatus = async (id: string, status: string) => {
    const { error } = await supabase.from("orders").update({ order_status: status }).eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Status updated");
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, order_status: status } : o)));
  };

  const markReceived = async () => {
    if (!confirmId) return;
    setMarking(true);
    const receivedAt = new Date().toISOString();
    const { error } = await supabase
      .from("orders")
      .update({ payment_status: "received", received_at: receivedAt })
      .eq("id", confirmId);
    setMarking(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Payment marked as received");
    setOrders((prev) =>
      prev.map((o) =>
        o.id === confirmId ? { ...o, payment_status: "received", received_at: receivedAt } : o,
      ),
    );
    setConfirmId(null);
  };

  const undoReceived = async () => {
    if (!undoId) return;
    setMarking(true);
    const { error } = await supabase
      .from("orders")
      .update({ payment_status: "pending", received_at: null })
      .eq("id", undoId);
    setMarking(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Payment reverted to pending");
    setOrders((prev) =>
      prev.map((o) =>
        o.id === undoId ? { ...o, payment_status: "pending", received_at: null } : o,
      ),
    );
    setUndoId(null);
  };

  const viewDetails = async (order: any) => {
    setSelected(order);
    setItems([]);
    const { data } = await supabase.from("order_items").select("*").eq("order_id", order.id);
    setItems(data ?? []);
  };

  const buildInvoiceFromOrder = (order: any, orderItems: any[]) => ({
    invoiceNo: invoiceNoFromOrder(order),
    date: new Date(order.created_at).toISOString().slice(0, 10),
    customer: {
      name: order.customer_name,
      phone: order.customer_phone,
      email: order.customer_email,
      address: order.customer_address,
    },
    items: orderItems.map((i) => ({
      name: i.product_name,
      qty: Number(i.quantity),
      price: Number(i.price),
    })),
    shipping: Number(order.shipping_charge) || 0,
    discount: 0,
    tax: 0,
    notes: `Order #${order.id.slice(0, 8)} • Thank you for shopping with Rupshoili!`,
  });

  const downloadInvoice = () => {
    if (!selected) return;
    try {
      const doc = buildInvoicePdf(buildInvoiceFromOrder(selected, items));
      doc.save(`${invoiceNoFromOrder(selected)}.pdf`);
      toast.success("Invoice downloaded");
    } catch (e: any) {
      toast.error(e.message || "Failed to generate invoice");
    }
  };

  const printInvoice = () => {
    if (!selected) return;
    try {
      const doc = buildInvoicePdf(buildInvoiceFromOrder(selected, items));
      doc.autoPrint();
      const url = doc.output("bloburl");
      const w = window.open(url, "_blank");
      if (!w) toast.error("Allow pop-ups to print");
    } catch (e: any) {
      toast.error(e.message || "Failed to print invoice");
    }
  };

  const deleteOrder = async () => {
    if (!deleteId) return;
    setDeleting(true);
    await supabase.from("order_items").delete().eq("order_id", deleteId);
    const { error } = await supabase.from("orders").delete().eq("id", deleteId);
    setDeleting(false);
    if (error) { toast.error(error.message); return; }
    toast.success("Order deleted");
    setOrders((prev) => prev.filter((o) => o.id !== deleteId));
    setDeleteId(null);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold">Orders</h1>
        <p className="text-sm text-muted-foreground">Manage customer orders and payments</p>
      </div>

      {/* Revenue summary */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="p-5 border-0 shadow-soft">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Received revenue</p>
          <p className="font-display text-2xl font-bold mt-1">{formatCurrency(totalRevenue, symbol)}</p>
        </Card>
        <Card className="p-5 border-0 shadow-soft">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Pending payments</p>
          <p className="font-display text-2xl font-bold mt-1">{pendingCount}</p>
        </Card>
        <Card className="p-5 border-0 shadow-soft">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Total orders</p>
          <p className="font-display text-2xl font-bold mt-1">{orders.length}</p>
        </Card>
      </div>

      <Card className="border-0 shadow-soft overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left p-4">Order</th>
                <th className="text-left p-4">Customer</th>
                <th className="text-left p-4 hidden md:table-cell">Date</th>
                <th className="text-right p-4">Total</th>
                <th className="text-left p-4">Payment</th>
                <th className="text-left p-4 hidden lg:table-cell">Received at</th>
                <th className="text-left p-4">Status</th>
                <th className="text-right p-4">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={8} className="p-4"><Skeleton className="h-20" /></td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">No orders yet.</td></tr>
              ) : (
                orders.map((o) => {
                  const received = o.payment_status === "received";
                  const stop = (e: React.MouseEvent) => e.stopPropagation();
                  return (
                    <tr
                      key={o.id}
                      onClick={() => viewDetails(o)}
                      className="hover:bg-muted/30 transition-smooth cursor-pointer"
                    >
                      <td className="p-4">
                        <span className="font-mono text-xs text-primary">#{o.id.slice(0, 8)}</span>
                      </td>
                      <td className="p-4">
                        <p className="text-sm font-medium">{o.customer_name}</p>
                        <p className="text-xs text-muted-foreground">{o.customer_email}</p>
                      </td>
                      <td className="p-4 text-sm text-muted-foreground hidden md:table-cell">{new Date(o.created_at).toLocaleDateString()}</td>
                      <td className="p-4 text-right font-medium">{formatCurrency(Number(o.total_amount), symbol)}</td>
                      <td className="p-4"><PaymentBadge status={o.payment_status} /></td>
                      <td className="p-4 hidden lg:table-cell text-xs text-muted-foreground">
                        {o.received_at
                          ? new Date(o.received_at).toLocaleString(undefined, {
                              day: "2-digit", month: "short", year: "numeric",
                              hour: "numeric", minute: "2-digit",
                            })
                          : "—"}
                      </td>
                      <td className="p-4" onClick={stop}>
                        <Select value={o.order_status} onValueChange={(v) => updateStatus(o.id, v)}>
                          <SelectTrigger className="h-8 w-32 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4 text-right" onClick={stop}>
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewDetails(o)}
                            className="h-8 text-xs gap-1"
                          >
                            <FileText className="h-3 w-3" /> Invoice
                          </Button>
                          {received ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setUndoId(o.id)}
                              className="h-8 text-xs gap-1"
                            >
                              <Undo2 className="h-3 w-3" /> Undo
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="accent"
                              onClick={() => setConfirmId(o.id)}
                              className="h-8 text-xs"
                            >
                              Receive
                            </Button>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => setDeleteId(o.id)}
                            className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                            aria-label="Delete order"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Invoice {selected ? invoiceNoFromOrder(selected) : ""}
            </DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 animate-fade-in">
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={downloadInvoice} className="gap-2" disabled={items.length === 0}>
                  <Download className="h-4 w-4" /> Download PDF
                </Button>
                <Button size="sm" variant="outline" onClick={printInvoice} className="gap-2" disabled={items.length === 0}>
                  <Printer className="h-4 w-4" /> Print
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><p className="text-muted-foreground">Order ID</p><p className="font-mono text-xs">#{selected.id.slice(0, 8)}</p></div>
                <div><p className="text-muted-foreground">Date</p><p>{new Date(selected.created_at).toLocaleString()}</p></div>
                <div><p className="text-muted-foreground">Customer</p><p className="font-medium">{selected.customer_name}</p></div>
                <div><p className="text-muted-foreground">Email</p><p>{selected.customer_email}</p></div>
                <div><p className="text-muted-foreground">Phone</p><p>{selected.customer_phone}</p></div>
                <div><p className="text-muted-foreground">Order status</p><p className="capitalize">{selected.order_status}</p></div>
                <div><p className="text-muted-foreground">Payment</p><div><PaymentBadge status={selected.payment_status} /></div></div>
                <div>
                  <p className="text-muted-foreground">Received at</p>
                  <p>{selected.received_at ? new Date(selected.received_at).toLocaleString() : "—"}</p>
                </div>
                <div className="col-span-2"><p className="text-muted-foreground">Address</p><p>{selected.customer_address}</p></div>
              </div>
              <div className="border-t pt-4">
                <p className="font-medium mb-2">Items</p>
                <div className="space-y-2">
                  {items.length === 0 ? (
                    <Skeleton className="h-12" />
                  ) : (
                    items.map((i) => (
                      <div key={i.id} className="flex justify-between text-sm">
                        <span>
                          {i.product_name}
                          {i.size && <span className="text-muted-foreground"> • Size {i.size}</span>}
                          {i.color && (
                            <span className="text-muted-foreground inline-flex items-center gap-1 ml-1">
                              • Color
                              <span className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-foreground/15" style={{ background: i.color }} />
                              {i.color}
                            </span>
                          )}
                          {" "}× {i.quantity}
                        </span>
                        <span>{formatCurrency(Number(i.price) * i.quantity, symbol)}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="border-t pt-3 space-y-1 text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span>{formatCurrency(Number(selected.subtotal), symbol)}</span></div>
                <div className="flex justify-between"><span>Shipping</span><span>{formatCurrency(Number(selected.shipping_charge), symbol)}</span></div>
                <div className="flex justify-between font-display text-lg font-bold"><span>Total</span><span>{formatCurrency(Number(selected.total_amount), symbol)}</span></div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark payment as received?</AlertDialogTitle>
            <AlertDialogDescription>
              This will add the order amount to your total revenue. You can undo this later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={marking}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={markReceived} disabled={marking}>
              {marking ? "Marking..." : "Mark as received"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!undoId} onOpenChange={(o) => !o && setUndoId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to undo this payment?</AlertDialogTitle>
            <AlertDialogDescription>
              The payment status will revert to <strong>Pending</strong> and the amount will be removed from your revenue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={marking}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={undoReceived} disabled={marking}>
              {marking ? "Reverting..." : "Yes, undo"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this order?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the order and all its items from your records. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteOrder}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? "Deleting..." : "Yes, delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminOrders;
